package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.AnimationAst
import top.wcpe.allincore.dsl.compiler.ast.CanvasAst
import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.DrawAst
import top.wcpe.allincore.dsl.compiler.ast.DrawInstructionAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst
import top.wcpe.allincore.dsl.compiler.semantic.AnimationRefs

/**
 * 组件 animation 命名空间改写器。
 *
 * 把组件内部声明的 animation id（裸名，如 "dialogIn"）以及组件 root / prop 默认值
 * 中对这些自有 animation 的引用，统一改写为 `${componentId}.${id}` 形式，避免
 * 不同组件 / screen 之间命名冲突。
 *
 * 改写范围：
 * - [ComponentAst.animations] 列表中的每个 [AnimationAst.id]
 * - [ComponentAst.props] 中类型为 STRING 的 prop 默认值：若为命中自有 id 的字符串
 *   字面量则前缀化（让组件作者在 prop 默认值里写裸名也能通）
 * - [ComponentAst.root] 内所有 [ElementAst.PropAssign]：prop 名命中
 *   [AnimationRefs.NAMES] 且值是 [ExprAst.Literal.StringLit] 时，按值是否在
 *   组件自有 animation id 集合内决定是否前缀化
 * - 所有表达式中的 [ExprAst.BuiltinCallExpr]：name == "animate" 时，第一个
 *   字符串字面量实参（位置参或命名参 "name"）按同样规则前缀化
 *
 * 不改写：
 * - 组件 root 中引用 screen 级 animation（字面量值不在自有 id 集合内时原样保留）
 * - [ExprAst.PropsRef] 与其它非字符串字面量表达式（由 [ExpressionSubstitutor]
 *   后续处理）
 *
 * 设计上无状态，按传入 component 的 id + animation 集合一次性 deep transform。
 */
internal object ComponentAnimationNamespacer {

    /** 入口：把 component 自有 animation 名空间化，返回新的不可变 [ComponentAst]。 */
    fun apply(comp: ComponentAst): ComponentAst {
        if (comp.animations.isEmpty()) return comp
        val localIds = comp.animations.mapTo(LinkedHashSet()) { it.id }
        val prefix = "${comp.id}."
        val newAnimations = comp.animations.map { it.copy(id = prefix + it.id) }
        val newProps = comp.props.map { decl -> transformPropDecl(decl, localIds, prefix) }
        val newRoot = transformElement(comp.root, localIds, prefix)
        return comp.copy(props = newProps, root = newRoot, animations = newAnimations)
    }

    /**
     * 仅对 STRING 类型 prop 的默认值做命中前缀化处理；其它类型保持原样。
     *
     * 限制类型避免误伤：localIds 通常是合法标识符，与普通字符串默认值很难碰撞，
     * 但限定 STRING 还能进一步把改写面收敛到"有可能装动画 id 的 prop"。
     */
    private fun transformPropDecl(
        decl: ComponentAst.PropDecl,
        localIds: Set<String>,
        prefix: String,
    ): ComponentAst.PropDecl {
        if (decl.type != ComponentAst.PropType.STRING) return decl
        val def = decl.defaultValue ?: return decl
        if (def !is ExprAst.Literal.StringLit) return decl
        if (def.value !in localIds) return decl
        return decl.copy(defaultValue = def.copy(value = prefix + def.value))
    }

    // ===== 元素树 =====

    private fun transformElement(
        element: ElementAst,
        localIds: Set<String>,
        prefix: String,
    ): ElementAst {
        if (element.isChildrenSlot) return element
        val newProps = element.props.map { transformPropAssign(it, localIds, prefix) }
        val newModifiers = element.modifiers.map { modifier ->
            modifier.copy(fields = modifier.fields.map { transformPropAssign(it, localIds, prefix) })
        }
        val newChildren = element.children.map { transformElement(it, localIds, prefix) }
        val newMenuBlocks = element.menuBlocks.map { menu ->
            menu.copy(
                props = menu.props.map { transformPropAssign(it, localIds, prefix) },
                root = transformElement(menu.root, localIds, prefix),
            )
        }
        val newCanvasData = element.canvasData?.let { transformCanvas(it, localIds, prefix) }
        val newSourceExpr = element.sourceExpr?.let { transformExpr(it, localIds, prefix) }
        val newTooltip = element.tooltipContent?.let { transformTooltip(it, localIds, prefix) }
        val newToast = element.toastContent?.let { transformTooltip(it, localIds, prefix) }
        val newRightClick = element.rightClickMenuContent?.let {
            transformTooltipComponent(it, localIds, prefix)
        }
        val newContextMenu = element.contextMenuContent?.let {
            transformTooltipComponent(it, localIds, prefix)
        }
        return element.copy(
            props = newProps,
            modifiers = newModifiers,
            children = newChildren,
            menuBlocks = newMenuBlocks,
            canvasData = newCanvasData,
            sourceExpr = newSourceExpr,
            tooltipContent = newTooltip,
            toastContent = newToast,
            rightClickMenuContent = newRightClick,
            contextMenuContent = newContextMenu,
        )
    }

    private fun transformPropAssign(
        pa: ElementAst.PropAssign,
        localIds: Set<String>,
        prefix: String,
    ): ElementAst.PropAssign {
        // AnimationRefs prop（show / hide / animation 等）：值若是命中 localIds 的字符串字面量则前缀化
        val isAnimationRef = pa.name in AnimationRefs.NAMES
        val newValue = if (isAnimationRef) {
            rewriteAnimationLiteral(pa.value, localIds, prefix)
        } else {
            transformExpr(pa.value, localIds, prefix)
        }
        return pa.copy(value = newValue)
    }

    /** 命中 localIds 时返回前缀化后的 StringLit；否则继续 transformExpr 兜底（处理嵌套 animate 调用）。 */
    private fun rewriteAnimationLiteral(
        value: ExprAst,
        localIds: Set<String>,
        prefix: String,
    ): ExprAst {
        if (value is ExprAst.Literal.StringLit && value.value in localIds) {
            return value.copy(value = prefix + value.value)
        }
        return transformExpr(value, localIds, prefix)
    }

    private fun transformTooltip(
        t: TooltipContentAst,
        localIds: Set<String>,
        prefix: String,
    ): TooltipContentAst = when (t) {
        is TooltipContentAst.Text -> t.copy(value = transformExpr(t.value, localIds, prefix))
        is TooltipContentAst.Inline -> t.copy(root = transformElement(t.root, localIds, prefix))
        is TooltipContentAst.Component -> transformTooltipComponent(t, localIds, prefix)
    }

    private fun transformTooltipComponent(
        t: TooltipContentAst.Component,
        localIds: Set<String>,
        prefix: String,
    ): TooltipContentAst.Component {
        return t.copy(props = t.props.map { transformPropAssign(it, localIds, prefix) })
    }

    // ===== canvas / draw 块 =====

    private fun transformCanvas(c: CanvasAst, localIds: Set<String>, prefix: String): CanvasAst {
        return c.copy(
            width = transformExpr(c.width, localIds, prefix),
            height = transformExpr(c.height, localIds, prefix),
            durationMs = c.durationMs?.let { transformExpr(it, localIds, prefix) },
            attrs = c.attrs.map { transformPropAssign(it, localIds, prefix) },
            drawBlock = transformDrawAst(c.drawBlock, localIds, prefix),
            onceBlock = c.onceBlock?.let { transformDrawAst(it, localIds, prefix) },
        )
    }

    private fun transformDrawAst(d: DrawAst, localIds: Set<String>, prefix: String): DrawAst {
        return d.copy(instructions = d.instructions.map { transformDrawInstruction(it, localIds, prefix) })
    }

    private fun transformDrawInstruction(
        ins: DrawInstructionAst,
        localIds: Set<String>,
        prefix: String,
    ): DrawInstructionAst = when (ins) {
        is DrawInstructionAst.Primitive -> ins.copy(
            args = ins.args.map { transformExpr(it, localIds, prefix) },
        )
        is DrawInstructionAst.ValAssign -> ins.copy(value = transformExpr(ins.value, localIds, prefix))
        is DrawInstructionAst.For -> ins.copy(
            body = ins.body.map { transformDrawInstruction(it, localIds, prefix) },
        )
        is DrawInstructionAst.IfElse -> ins.copy(
            condition = transformExpr(ins.condition, localIds, prefix),
            thenBranch = ins.thenBranch.map { transformDrawInstruction(it, localIds, prefix) },
            elseBranch = ins.elseBranch.map { transformDrawInstruction(it, localIds, prefix) },
        )
    }

    // ===== 表达式 =====

    private fun transformExpr(expr: ExprAst, localIds: Set<String>, prefix: String): ExprAst = when (expr) {
        is ExprAst.BuiltinCallExpr -> transformBuiltinCallExpr(expr, localIds, prefix)
        is ExprAst.BinaryOp -> expr.copy(
            left = transformExpr(expr.left, localIds, prefix),
            right = transformExpr(expr.right, localIds, prefix),
        )
        is ExprAst.UnaryOp -> expr.copy(operand = transformExpr(expr.operand, localIds, prefix))
        is ExprAst.MemberAccess -> expr.copy(target = transformExpr(expr.target, localIds, prefix))
        is ExprAst.SafeMemberAccess -> expr.copy(target = transformExpr(expr.target, localIds, prefix))
        // M10 Stage 6：`target!!` 透传 target 子表达式
        is ExprAst.NonNullAssert -> expr.copy(target = transformExpr(expr.target, localIds, prefix))
        is ExprAst.MemberCall -> expr.copy(
            target = transformExpr(expr.target, localIds, prefix),
            args = expr.args.map { transformExpr(it, localIds, prefix) },
            namedArgs = expr.namedArgs.map { it.copy(value = transformExpr(it.value, localIds, prefix)) },
        )
        is ExprAst.SafeMemberCall -> expr.copy(
            target = transformExpr(expr.target, localIds, prefix),
            args = expr.args.map { transformExpr(it, localIds, prefix) },
        )
        is ExprAst.Elvis -> expr.copy(
            left = transformExpr(expr.left, localIds, prefix),
            right = transformExpr(expr.right, localIds, prefix),
        )
        is ExprAst.Ternary -> expr.copy(
            cond = transformExpr(expr.cond, localIds, prefix),
            thenExpr = transformExpr(expr.thenExpr, localIds, prefix),
            elseExpr = transformExpr(expr.elseExpr, localIds, prefix),
        )
        is ExprAst.Interp -> expr.copy(parts = expr.parts.map { part ->
            when (part) {
                is ExprAst.Interp.InterpPart.ExprPart ->
                    ExprAst.Interp.InterpPart.ExprPart(transformExpr(part.expr, localIds, prefix))
                is ExprAst.Interp.InterpPart.TextPart -> part
            }
        })
        is ExprAst.ListLiteralExpr -> expr.copy(elements = expr.elements.map { transformExpr(it, localIds, prefix) })
        is ExprAst.MapLiteralExpr -> expr.copy(
            entries = expr.entries.map { it.copy(value = transformExpr(it.value, localIds, prefix)) },
        )
        is ExprAst.RangeExpr -> expr.copy(
            start = transformExpr(expr.start, localIds, prefix),
            endValue = transformExpr(expr.endValue, localIds, prefix),
        )
        is ExprAst.IndexExpr -> expr.copy(
            target = transformExpr(expr.target, localIds, prefix),
            index = transformExpr(expr.index, localIds, prefix),
        )
        is ExprAst.CallExpr -> expr.copy(
            callee = transformExpr(expr.callee, localIds, prefix),
            args = expr.args.map { transformExpr(it, localIds, prefix) },
        )
        is ExprAst.IfExpr -> expr.copy(
            cond = transformExpr(expr.cond, localIds, prefix),
            thenBody = expr.thenBody.map { transformStmt(it, localIds, prefix) },
            elseBody = expr.elseBody.map { transformStmt(it, localIds, prefix) },
        )
        is ExprAst.LambdaExpr -> expr.copy(body = expr.body.map { transformStmt(it, localIds, prefix) })
        is ExprAst.WhenExpr -> expr.copy(
            subject = transformExpr(expr.subject, localIds, prefix),
            branches = expr.branches.map { br ->
                br.copy(
                    values = br.values.map { transformExpr(it, localIds, prefix) },
                    body = br.body.map { transformStmt(it, localIds, prefix) },
                )
            },
            elseBody = expr.elseBody?.map { transformStmt(it, localIds, prefix) },
        )
        is ExprAst.NetRequestCall -> expr.copy(
            payload = expr.payload.map { it.copy(value = transformExpr(it.value, localIds, prefix)) },
            callback = expr.callback?.let { transformExpr(it, localIds, prefix) },
            timeoutMs = expr.timeoutMs?.let { transformExpr(it, localIds, prefix) },
        )
        is ExprAst.NetSubscribeCall -> expr.copy(
            handler = transformExpr(expr.handler, localIds, prefix),
        )
        is ExprAst.AwaitExpr -> expr.copy(
            netRequestCall = expr.netRequestCall?.let { transformExpr(it, localIds, prefix) },
            suspendCall = expr.suspendCall?.let { transformExpr(it, localIds, prefix) as ExprAst.CallExpr },
            tailExpr = expr.tailExpr?.let { transformExpr(it, localIds, prefix) },
        )
        is ExprAst.IsExpr -> expr.copy(target = transformExpr(expr.target, localIds, prefix))
        // 叶子节点：原样返回（StringLit 在元素 prop / animate 调用上下文之外不应被改写）
        is ExprAst.Literal,
        is ExprAst.NullLit,
        is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef,
        is ExprAst.IdentRef,
        is ExprAst.PropsRef,
        is ExprAst.LocalRef,
        is ExprAst.ThemeRef,
        is ExprAst.FunRefExpr -> expr
    }

    /** `animate(name, ...)`：第一个 StringLit 实参（位置 0 或命名 "name"）按 localIds 命中前缀化。 */
    private fun transformBuiltinCallExpr(
        expr: ExprAst.BuiltinCallExpr,
        localIds: Set<String>,
        prefix: String,
    ): ExprAst.BuiltinCallExpr {
        val isAnimateCall = expr.name == "animate"
        val newArgs = expr.args.mapIndexed { idx, arg ->
            if (isAnimateCall && idx == 0) {
                rewriteAnimationLiteral(arg, localIds, prefix)
            } else {
                transformExpr(arg, localIds, prefix)
            }
        }
        val newNamed = expr.namedArgs.map { na ->
            val newValue = if (isAnimateCall && na.name == "name") {
                rewriteAnimationLiteral(na.value, localIds, prefix)
            } else {
                transformExpr(na.value, localIds, prefix)
            }
            na.copy(value = newValue)
        }
        return expr.copy(args = newArgs, namedArgs = newNamed)
    }

    private fun transformStmt(stmt: StmtAst, localIds: Set<String>, prefix: String): StmtAst = when (stmt) {
        is StmtAst.ExprStmt -> stmt.copy(call = transformExpr(stmt.call, localIds, prefix))
        is StmtAst.AssignStmt -> stmt.copy(
            lhs = transformLValue(stmt.lhs, localIds, prefix),
            rhs = transformExpr(stmt.rhs, localIds, prefix),
        )
        is StmtAst.VarDeclStmt -> stmt.copy(initializer = transformExpr(stmt.initializer, localIds, prefix))
        is StmtAst.IfStmt -> stmt.copy(
            cond = transformExpr(stmt.cond, localIds, prefix),
            thenBody = stmt.thenBody.map { transformStmt(it, localIds, prefix) },
            elseBody = stmt.elseBody.map { transformStmt(it, localIds, prefix) },
        )
        is StmtAst.WhileStmt -> stmt.copy(
            cond = transformExpr(stmt.cond, localIds, prefix),
            body = stmt.body.map { transformStmt(it, localIds, prefix) },
        )
        is StmtAst.ForInStmt -> stmt.copy(
            iterable = transformExpr(stmt.iterable, localIds, prefix),
            body = stmt.body.map { transformStmt(it, localIds, prefix) },
        )
        is StmtAst.WhenStmt -> stmt.copy(whenExpr = transformExpr(stmt.whenExpr, localIds, prefix) as ExprAst.WhenExpr)
        is StmtAst.ReturnStmt -> stmt.copy(value = stmt.value?.let { transformExpr(it, localIds, prefix) })
        is StmtAst.TryStmt -> stmt.copy(
            tryBody = stmt.tryBody.map { transformStmt(it, localIds, prefix) },
            catchBody = stmt.catchBody.map { transformStmt(it, localIds, prefix) },
            finallyBody = stmt.finallyBody.map { transformStmt(it, localIds, prefix) },
        )
        // batch 语句：对 body 递归做 namespacing 并返回 copy（与 TryStmt 的 tryBody 处理一致）
        is StmtAst.BatchStmt -> stmt.copy(
            body = stmt.body.map { transformStmt(it, localIds, prefix) },
        )
        is StmtAst.FunDeclStmt -> stmt.copy(
            decl = stmt.decl.copy(body = stmt.decl.body.map { transformStmt(it, localIds, prefix) }),
        )
        is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> stmt
    }

    private fun transformLValue(lv: LValueAst, localIds: Set<String>, prefix: String): LValueAst = when (lv) {
        is LValueAst.StateLValue, is LValueAst.SessionLValue,
        is LValueAst.PersistLValue, is LValueAst.LocalLValue -> lv
        is LValueAst.IndexLValue -> lv.copy(
            target = transformExpr(lv.target, localIds, prefix),
            index = transformExpr(lv.index, localIds, prefix),
        )
    }
}
